/**
 * Endpoints de autenticación de EDSuite API.
 *
 * Nota: estas rutas están excluidas del API key en el backend, por lo que
 * `auth: false` evita enviar un token inexistente en el login.
 */

import { apiClient } from '@/lib/http/api-client';

import type {
  AuthUser,
  BiometriaPayload,
  LoginBiometriaResponse,
  LoginPayload,
  LoginResponse,
} from './types';

export const authApi = {
  login: (payload: LoginPayload): Promise<LoginResponse> =>
    apiClient.post<LoginResponse>('/api/auth/login', {
      body: payload,
      auth: false,
      retryOnAuthError: false,
      credentials: 'include',
    }),

  /**
   * Inicia sesión con la cara: la credencial es el descriptor de la captura, así que no hay token
   * previo ni contraseña. La cookie de refresco se emite igual que en el login normal.
   */
  loginBiometria: (payload: BiometriaPayload): Promise<LoginBiometriaResponse> =>
    apiClient.post<LoginBiometriaResponse>('/api/auth/login-biometria', {
      body: payload,
      auth: false,
      retryOnAuthError: false,
      credentials: 'include',
    }),

  /** Renueva el par de tokens usando la cookie httpOnly del refresh token. */
  refresh: (): Promise<LoginResponse> =>
    apiClient.post<LoginResponse>('/api/auth/refresh', {
      auth: false,
      retryOnAuthError: false,
      credentials: 'include',
    }),

  /** Sesión actual según el access token en memoria. */
  me: (signal?: AbortSignal): Promise<{ user: AuthUser }> =>
    apiClient.get<{ user: AuthUser }>('/api/auth/me', { signal, retryOnAuthError: false }),

  /** Cierre de sesión en servidor (revoca los refresh tokens y limpia la cookie). */
  logout: (): Promise<{ message: string }> =>
    apiClient.post<{ message: string }>('/api/auth/logout', {
      retryOnAuthError: false,
      credentials: 'include',
    }),

  /** Cambio de contraseña autoservicio. */
  cambiarContrasena: (body: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ message: string }> =>
    apiClient.post<{ message: string }>('/api/auth/change-password', {
      body,
      retryOnAuthError: false,
    }),
} as const;
